/**
 * Shared types for pipe detection. Kept THREE-free so the algorithm layer
 * (`lib/pipe/*`) stays pure and unit-testable against synthetic point arrays.
 *
 * All coordinates are world-space, project SRID, Z-up — the same frame the
 * `walkPointCloud` callback hands out.
 */

export type Vec3 = [number, number, number];

/**
 * A fitted straight cylinder (pipe segment). `axis` is a unit vector; the
 * pipe runs from `pStart` to `pEnd` along it. `center` is an arbitrary point
 * on the axis (the circle-fit centre of the supported cross-section) — the
 * endpoints are the authoritative extent.
 */
export interface CylinderFit {
  /** Unit axis direction. */
  axis: Vec3;
  /** A point on the centerline (circle-fit centre). */
  center: Vec3;
  /** Pipe radius (metres). */
  radius: number;
  /** Centerline start (world). */
  pStart: Vec3;
  /** Centerline end (world). */
  pEnd: Vec3;
}

/**
 * Confidence / quality signals. Because radius is the fragile parameter on
 * partial arcs (and v1 has no nominal-size catalog to snap to), the UI uses
 * these to warn the user and foreground manual correction.
 */
export interface FitQuality {
  /** Angular span of circumference actually observed, in degrees (0–360). */
  arcCoverageDeg: number;
  /** RMS of radial residuals over inliers (metres). */
  rmsM: number;
  /** Inlier count the final fit was computed from. */
  inlierCount: number;
  /**
   * Axis well-conditioned-ness in [0,1]. Derived from the eigenvalue gap of
   * the normal-covariance matrix — low when the visible arc is too thin to
   * pin the axis down.
   */
  axisConfidence: number;
  /** Overall 0–1 confidence (combines arc coverage, rms, axis condition). */
  confidence: number;
}

export interface PipeResult extends CylinderFit, FitQuality {
  /** Length along the axis (|pEnd − pStart|). */
  lengthM: number;
  /** How many points were loaded + considered across all phases. */
  pointsConsidered: number;
  /** Wall-clock of the whole detect call. */
  durationMs: number;
}

export type PipeDetectPhase =
  /** Loading the small fine box around the click. */
  | 'seed'
  /** Fitting the seed cross-section (normals + circle). */
  | 'fitting'
  /** Loading + growing along the corridor. */
  | 'growing';

export interface PipeDetectOptions {
  /** Half-size (m) of the initial fine box around the seed click. */
  seedHalfSize?: number;
  /**
   * Target octree spacing (m) for the seed-box load. A fixed moderate LOD
   * (rather than finest) keeps the load fast and uniformly covers the box on
   * dense clouds, while staying fine enough to resolve normals on small pipes.
   */
  seedSpacing?: number;
  /** Max length (m) to search along the axis in each direction from the seed. */
  maxSearchLength?: number;
  /**
   * Inlier band half-width as a fraction of the radius. A point counts as
   * "on the pipe surface" when |distToAxis − r| < bandFrac·r + bandFloor.
   */
  bandFrac?: number;
  /** Absolute floor (m) added to the inlier band, for thin pipes / noise. */
  bandFloor?: number;
  /** Hard cap on total points collected (seed + corridor). */
  maxPoints?: number;
  /** Wall-clock budget (ms) for the grow phase before it stops early. */
  growBudgetMs?: number;
  onProgress?: (phase: PipeDetectPhase, done: number, total: number) => void;
  signal?: AbortSignal;
}

export const DEFAULT_PIPE_OPTIONS: Required<Omit<PipeDetectOptions, 'onProgress' | 'signal'>> = {
  // Small box so the finest-LOD load stays fast and rarely caps (a smaller box
  // touches far fewer octree nodes). Region growing isolates the pipe from
  // surroundings, and 0.35m still captures ~0.7m of pipe length — plenty for
  // the axis fit — across any pipe size we target.
  seedHalfSize: 0.35,
  // Near-finest: 2mm floor descends to the cloud's native leaves on typical
  // (≥2mm) clouds, so small pipes (sub-5cm) get the resolution they need, while
  // capping pathologically dense (sub-mm) clouds.
  seedSpacing: 0.002,
  maxSearchLength: 40,
  bandFrac: 0.15,
  bandFloor: 0.02,
  maxPoints: 150_000,
  growBudgetMs: 1200,
};
